import _ from 'lodash'
import { first, switchMap, tap} from 'rxjs/operators'

import { AuditCampaign, Record, ProjectLinkableItemStateResource } from '@skinsoft/types'
import { SEARCH_TYPES } from '@skinsoft/const'
import { auditCampaignSelectors } from '@skinsoft/selectors'
import { OfflineDataFetcher, AuditCampaignOfflineDataFetcher, AuditCampaignOfflineDataUpdateCacher } from '@skinsoft/offline'
import { EntityDataStore } from '@skinsoft/entity-data-store'
import { RecordDataStore } from '@skinsoft/entity'

const DEFAULT_ITEM_TYPE: string = "PhysicalMuseumItem"

export class AuditCampaignDataStore extends EntityDataStore<AuditCampaign>{
    public static getInstance() {
        if (!AuditCampaignDataStore.instance) {
            AuditCampaignDataStore.instance = new AuditCampaignDataStore();
        }

        return AuditCampaignDataStore.instance;
    }

    public static install(Vue) {
        Vue.prototype.$auditCampaignDataStore = AuditCampaignDataStore.getInstance();
    }

    private static instance: AuditCampaignDataStore = null as any;

    constructor() {
        super('audit-projects', [SEARCH_TYPES.auditCampaign]);
    }

    protected initializeDataFetcher() {
        this.offlineDataFetcher = new AuditCampaignOfflineDataFetcher()
    }

    protected initializeDataPusher() {
        this.offlineDataUpdateCacher = new AuditCampaignOfflineDataUpdateCacher()
    }

    public exists(id: AuditCampaign['id']) {
        return this.fetchOne(id)
            .pipe(first())
            .toPromise();
    }

    public fixLinkedItem(obj) {
        obj.recolementLinkSchema.linkedItem = { linkableItem: obj.recolementLinkSchema.linkedItem }
        return obj
    }

    public update(id: string, body: AuditCampaign, endpointOverride?: string, ignoreCachedStatus: boolean = false) {
        if (window.navigator.onLine) {
            // Saving records with local ids
            const auditRecordsBeforeUpdate = auditCampaignSelectors.auditRecords(body).sort();

            // Removing temporary record ids to avoid Error 500 on update
            if(body.auditProjectSchema?.auditReports) {
                body.auditProjectSchema.auditReports = auditCampaignSelectors.auditRecords(body).filter(recordId => {
                    return !OfflineDataFetcher.isTemporaryId(recordId)
                })
            }

            const linkableItemsBeforeUpdate = auditCampaignSelectors.linkableItems(body)

            if(body.workflowSchema?.linkableItems) {
                body.workflowSchema.linkableItems = auditCampaignSelectors.linkableItems(body).filter(linkableItem => {
                    return linkableItem.linkableItem?.linkableItem && 
                        !OfflineDataFetcher.isTemporaryId(linkableItem.linkableItem?.linkableItem)
                })
            }


            let needOfflineUpdate = true

            // Updating campaign to create missing records from linkable items
            return super.update(id, body, endpointOverride, ignoreCachedStatus).pipe(
                switchMap(response => this.fetchOne(id)),
                tap(
                    response => {
                        // Retieving records, including the new ones freshly created
                        const auditRecordsAfterUpdate = auditCampaignSelectors.auditRecords(response).sort();

                        // Getting the created records that are not in cache yet (? perfs with a lot of records ?)
                        const diff = auditRecordsAfterUpdate.filter(
                            recordId => !auditRecordsBeforeUpdate.includes(recordId)
                        )

                        // Replacing temp ids in local records and pushing them to db
                        Promise.all(auditRecordsBeforeUpdate
                            .filter(recordId => OfflineDataFetcher.isTemporaryId(recordId)) // keep only ids of locally created entities
                            .map(
                                async (recordId, index) => {
                                    const dbId = diff[index]
                                    const updateBody = (await RecordDataStore.getInstance().getObjectInUpdateCache(recordId)) as Record
                                    if (dbId) {
                                        const existingBody = await RecordDataStore.getInstance().fetchOne(dbId).pipe(first()).toPromise();

                                        needOfflineUpdate = false 
                                        
                                        // Replacing  temporary id in body
                                        updateBody.id = dbId;

                                        // Keeping readonly fields to avoid error on update
                                        _.set(updateBody, 'recolementIdentificationSchema.recoleur', _.get(existingBody, 'recolementIdentificationSchema.recoleur'));
                                        _.set(updateBody, 'recolementIdentificationSchema.authorizedAuditor', _.get(existingBody, 'recolementIdentificationSchema.authorizedAuditor'));
                                        _.set(updateBody, 'institutionSharing', _.get(existingBody, 'institutionSharing'));

                                        RecordDataStore.getInstance().update(dbId, updateBody, undefined, ignoreCachedStatus).subscribe(
                                            saveResponse => {
                                                // Removing freshly saved record from cache
                                                RecordDataStore.getInstance().removeObjectFromUpdateCache(recordId);
                                            }
                                        );
                                    }
                                }
                            ))
                        if (needOfflineUpdate) {
                            if(linkableItemsBeforeUpdate) {
                                _.set(
                                    body,
                                    'workflowSchema.linkableItems',
                                    linkableItemsBeforeUpdate
                                )
                            }
                            if(auditRecordsBeforeUpdate) {
                                _.set(
                                    body,
                                    'auditProjectSchema.auditReports',
                                    auditRecordsBeforeUpdate
                                )
                            }
                            super.updateOfflineMode(id, body)
                        }
                    })
            );
        }
        return super.update(id, body, endpointOverride, ignoreCachedStatus);
    }

    public async addItem(
        auditCampaign: AuditCampaign,
        newItemId: string,
        itemType: string = DEFAULT_ITEM_TYPE,
        ignoreCacheStatus: boolean = false,
    ): Promise<any> {
        const linkableItems: ProjectLinkableItemStateResource[] = auditCampaignSelectors.linkableItems(auditCampaign);
        const newLinkedItem: ProjectLinkableItemStateResource = {
            linkableItem: {
                linkableItem: newItemId,
                linkableItemReferenceType: itemType,   
            },
        }
        linkableItems.push(newLinkedItem)

        let updatedAuditCampaign = _.cloneDeep(auditCampaign)

        _.set(
            updatedAuditCampaign,
            'workflowSchema.linkableItems',
            linkableItems
        )

        // Creating local record if offline mode or item is not online yet
        if (!window.navigator.onLine || OfflineDataFetcher.isTemporaryId(newItemId)) {
            const newRecord = await RecordDataStore.getInstance().createOfflineRecordForCampaign(
                newLinkedItem.linkableItem!.linkableItem!,
                newLinkedItem.linkableItem!.linkableItemReferenceType!,
                auditCampaign.id
            ).toPromise()

            if (newRecord) {
                let updatedAuditReports = _.get(
                    updatedAuditCampaign, 
                    'auditProjectSchema.auditReports',
                    []
                )
                updatedAuditReports.push(newRecord.id)

                _.set(
                    updatedAuditCampaign,
                    'auditProjectSchema.auditReports',
                    updatedAuditReports
                )
            }
        }

        return await this.update(auditCampaign.id, updatedAuditCampaign, undefined, ignoreCacheStatus)
            .pipe(first())
            .toPromise()
    }
}